import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaQuestionCircle,
  FaLayerGroup,
  FaComments,
  FaBookOpen,
} from "react-icons/fa";
import DOMPurify from "dompurify";
import ButtonEdit from "../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../components/Button/ButtonDelete";
import {
  getAllFaqs,
  deleteFaq,
} from "../../../../services/faqServices";
import AddFaqModal from "./AddFaqModal";
import EditFaqModal from "./EditFaqModal";
import { ToastContainer, toast } from "react-toastify";
import useScrollToTop from "../../../../hooks/useScrollToTop";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";

const HERO =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";

const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";

const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html);
}

function htmlToPlainText(html = "") {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  return clean.replace(/\s+/g, " ").trim();
}

function truncate(str = "", max = 160) {
  if (!str) return "—";
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
}

function StatCard({ icon, label, value, hint, dark = false }) {
  return (
    <div
      className={
        dark
          ? "rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md"
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              dark ? "text-white/60" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>

          <div
            className={`mt-2 text-2xl font-semibold ${
              dark ? "text-white" : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>

          {hint ? (
            <div
              className={`mt-1 text-xs ${
                dark ? "text-white/70" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`rounded-2xl p-3 ${
            dark
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description }) {
  return (
    <div className="mb-5">
      {eyebrow ? (
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
          {eyebrow}
        </div>
      ) : null}

      <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h2>

      {description ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
    orange:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    pink: "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td
      className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}
    >
      {children}
    </td>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
        ❔
      </div>

      <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        Aucune FAQ trouvée
      </div>

      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Ajoute une FAQ ou élargis ta recherche.
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3 p-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white p-3 dark:bg-slate-900">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>

      <div className="mt-1 break-words font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

const ListingFaq = () => {
  useScrollToTop();

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState("category_asc");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFaqId, setEditFaqId] = useState(null);

  const fetchFaqs = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllFaqs();
      setFaqs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des FAQ :", error);
      toast.error("Erreur lors du chargement des FAQ");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const categoryOptions = useMemo(() => {
    const set = new Set();

    faqs.forEach((faq) => {
      if (faq.categorie) set.add(faq.categorie);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [faqs]);

  const displayedFaqs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let rows = [...faqs];

    if (term) {
      rows = rows.filter((faq) => {
        const haystack = [
          faq.categorie,
          faq.titre,
          htmlToPlainText(faq.contenu || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(term);
      });
    }

    if (selectedCategory) {
      rows = rows.filter((faq) => faq.categorie === selectedCategory);
    }

    rows.sort((a, b) => {
      switch (sortBy) {
        case "title_asc":
          return String(a.titre || "").localeCompare(String(b.titre || ""), "fr");

        case "title_desc":
          return String(b.titre || "").localeCompare(String(a.titre || ""), "fr");

        case "category_desc":
          return String(b.categorie || "").localeCompare(
            String(a.categorie || ""),
            "fr"
          );

        case "category_asc":
        default:
          return String(a.categorie || "").localeCompare(
            String(b.categorie || ""),
            "fr"
          );
      }
    });

    return rows;
  }, [faqs, searchTerm, selectedCategory, sortBy]);

  const stats = useMemo(() => {
    const categoriesCount = new Set(
      displayedFaqs.map((faq) => faq.categorie).filter(Boolean)
    ).size;

    const withAnswer = displayedFaqs.filter(
      (faq) => htmlToPlainText(faq.contenu || "").length > 0
    ).length;

    const longestAnswer = displayedFaqs.reduce((max, faq) => {
      const length = htmlToPlainText(faq.contenu || "").length;
      return Math.max(max, length);
    }, 0);

    return {
      total: displayedFaqs.length,
      categoriesCount,
      withAnswer,
      longestAnswer,
    };
  }, [displayedFaqs]);

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous supprimer cette FAQ ?")) {
      return;
    }

    try {
      await deleteFaq(id);
      toast.success("FAQ supprimée avec succès");
      setFaqs((prev) => prev.filter((faq) => faq.id !== id));
    } catch (error) {
      console.error("Erreur lors de la suppression de la FAQ :", error);
      toast.error("Erreur lors de la suppression de la FAQ");
    }
  };

  const openEditModal = (id) => {
    setEditFaqId(id);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setEditFaqId(null);
    setShowEditModal(false);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSortBy("category_asc");
  };

  const isEmpty = !loading && displayedFaqs.length === 0;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.16),transparent_30%)]" />

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaQuestionCircle />
                Support & réponses
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Gestion des FAQ
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Centralise les questions fréquentes, organise-les par catégorie
                et garde des réponses claires pour tes clients et utilisateurs.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className={`${BUTTON_SOFT} bg-white text-slate-900 hover:bg-slate-100`}
              >
                <FaPlus />
                Ajouter une FAQ
              </button>

              <button
                type="button"
                onClick={fetchFaqs}
                className={`${BUTTON_SOFT} border border-white/15 bg-white/10 text-white hover:bg-white/20`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              dark
              icon={<FaQuestionCircle />}
              label="FAQ"
              value={stats.total}
              hint="Résultats affichés"
            />

            <StatCard
              dark
              icon={<FaLayerGroup />}
              label="Catégories"
              value={stats.categoriesCount}
              hint="Groupes différents"
            />

            <StatCard
              dark
              icon={<FaComments />}
              label="Réponses"
              value={stats.withAnswer}
              hint="FAQ avec contenu"
            />

            <StatCard
              dark
              icon={<FaBookOpen />}
              label="Réponse max"
              value={`${stats.longestAnswer} car.`}
              hint="Longueur maximale"
            />
          </div>
        </div>
      </section>

      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Filtres"
          title="Retrouver rapidement une question"
          description="Recherche par catégorie, question ou contenu de réponse."
        />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="relative xl:col-span-6">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              className={`${INPUT} pl-11`}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une question ou une réponse..."
            />
          </div>

          <div className="xl:col-span-3">
            <select
              className={INPUT}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Toutes les catégories</option>
              {categoryOptions.map((categorie) => (
                <option key={categorie} value={categorie}>
                  {categorie}
                </option>
              ))}
            </select>
          </div>

          <div className="xl:col-span-2">
            <select
              className={INPUT}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="category_asc">Tri : Catégorie A → Z</option>
              <option value="category_desc">Tri : Catégorie Z → A</option>
              <option value="title_asc">Tri : Question A → Z</option>
              <option value="title_desc">Tri : Question Z → A</option>
            </select>
          </div>

          <div className="xl:col-span-1">
            <button
              type="button"
              onClick={resetFilters}
              className="w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Reset
            </button>
          </div>
        </div>
      </section>

      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Questions fréquentes
              </h2>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Liste complète des questions, réponses et catégories.
              </p>
            </div>

            <Badge tone="purple">{displayedFaqs.length} affichée(s)</Badge>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[1180px]">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Catégorie</Th>
                    <Th>Question</Th>
                    <Th>Réponse</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>

                <tbody>
                  {displayedFaqs.map((faq) => (
                    <tr
                      key={faq.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/70 dark:border-slate-800 dark:hover:bg-slate-800/30"
                    >
                      <Td className="min-w-[180px]">
                        <Badge tone="blue">{faq.categorie || "—"}</Badge>
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          #{faq.id}
                        </div>
                      </Td>

                      <Td className="min-w-[300px]">
                        <div className="font-semibold leading-6 text-slate-900 dark:text-slate-50">
                          {faq.titre || "Sans question"}
                        </div>
                      </Td>

                      <Td className="min-w-[460px] max-w-[580px]">
                        <div
                          className="prose prose-sm max-w-none leading-6 text-slate-600 dark:prose-invert dark:text-slate-300 line-clamp-4"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(faq.contenu || ""),
                          }}
                        />
                      </Td>

                      <Td className="min-w-[120px]">
                        <div className="flex justify-end gap-2">
                          <ButtonEdit
                            onClick={() => openEditModal(faq.id)}
                            hoverLabel="Éditer"
                          />
                          <ButtonDelete
                            onClick={() => handleDelete(faq.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-4 p-4 md:hidden">
              {displayedFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-800/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold leading-6 text-slate-900 dark:text-slate-50">
                        {faq.titre || "Sans question"}
                      </div>

                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        #{faq.id}
                      </div>
                    </div>

                    <Badge tone="blue">{faq.categorie || "—"}</Badge>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <MobileInfo
                      label="Réponse"
                      value={truncate(htmlToPlainText(faq.contenu || ""), 180)}
                    />
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <ButtonEdit
                      onClick={() => openEditModal(faq.id)}
                      hoverLabel="Éditer"
                    />
                    <ButtonDelete
                      onClick={() => handleDelete(faq.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <AddFaqModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        refreshList={fetchFaqs}
      />

      {showEditModal && editFaqId && (
        <EditFaqModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          faqId={editFaqId}
          refreshList={fetchFaqs}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default ListingFaq;